import { MEDIA_STALE_TIME } from '@/utils/media';
import {
    MediaPackType,
    MediaGuideWithSubtitles,
    MediaProgramMetadata,
    MediaProgramWithGuides,
} from '@/utils/media/types';
import { useCircle } from '@rhg/hooks/use-circle';
import createMediaV2Api from '@rhg/libs/api/create-media-v2-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    checkQueryCache,
    invalidateQueryKey,
    ItemToQueryMapping,
    queryClient,
    queryKeys,
    useQuery,
} from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { useMemo } from 'react';
import { useActiveCircleId } from '../use-active-circle';
import { cacheProgramMetadata } from '../use-pack-guide-program-metadata/cache';
import { invalidatePacks } from '../use-packs';
import { invalidatePrograms } from '../use-programs';

export const guidesQueryOptions = {
    /**
     * The key should be a Guide ID map where each Guide ID maps to its corresponding Guide ID for easy invalidation
     * This allows us to invalidate any query with a given Guide ID by passing in [queryKeys.Guides, circleId, { someGuideId: someGuideId }]
     *
     * Refs:
     * https://tanstack.com/query/latest/docs/framework/react/guides/query-invalidation#query-matching-with-invalidatequeries
     * https://tanstack.com/query/latest/docs/framework/react/guides/query-keys#array-keys-with-variables
     */
    queryKey: ({
        guideIds,
        circleId,
    }: {
        guideIds: Set<string>;
        circleId: number;
    }) => {
        return [
            queryKeys.guides,
            circleId,
            Array.from(guideIds).reduce(
                (prev, guideId) => ({
                    ...prev,
                    [guideId]: guideId,
                }),
                {} as Record<string, string>
            ),
        ];
    },
    queryFn: async ({
        getAccessTokenSilently,
        guideIds,
        circleId,
        circleAuthorizationToken,
        rHGApp,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        guideIds: Set<string>;
        circleId: number;
        circleAuthorizationToken: string;
        rHGApp: string;
    }) => {
        const mediaV2Api = createMediaV2Api(getAccessTokenSilently);

        const response = await mediaV2Api.v2GetGuides({
            circleAuthorizationToken,
            guideId: Array.from(guideIds),
            recipientId: circleId,
            rHGApp,
        });

        return response.data.programs as Array<
            MediaProgramMetadata & { guides: MediaGuideWithSubtitles[] }
        >;
    },
    queryOptions: ({
        guideIds,
        circleId,
        circleAuthorizationToken,
    }: {
        guideIds: Set<string>;
        circleId: number;
        circleAuthorizationToken: string;
    }) => {
        return {
            enabled: !!(
                circleId > 0 &&
                circleAuthorizationToken &&
                guideIds.size > 0
            ),
            staleTime: MEDIA_STALE_TIME,
            gcTime: MEDIA_STALE_TIME,
        };
    },
};

export function cacheGuides(
    guides: MediaGuideWithSubtitles[] | undefined,
    circleId: number,
    _queryClient = queryClient
) {
    (guides ?? []).forEach((guide) => {
        const key = guidesQueryOptions.queryKey({
            guideIds: new Set([guide.guideId]),
            circleId,
        });
        _queryClient.setQueryData<MediaGuideWithSubtitles[]>(key, [guide]);
    });
}

function guidesInProgram(
    program: MediaProgramWithGuides,
    guideIds: Set<string>
) {
    return !!program.guides?.some((guide) => guideIds.has(guide.guideId));
}

function programsEmbedGuides(data: unknown, guideIds: Set<string>) {
    return (
        Array.isArray(data) &&
        data.some((program: MediaProgramWithGuides) =>
            guidesInProgram(program, guideIds)
        )
    );
}

export async function invalidateGuides({
    guideIds,
    circleId,
    skipGuides = false,
}: {
    guideIds: Set<string>;
    circleId: number;
    /**
     * Pass skipGuides:true if you are calling in a mutation onSuccess callback
     * as the guide itself has already been updated,
     * you just want to invalidate the associated programs and packs
     */
    skipGuides?: boolean;
}) {
    if (!guideIds.size) return;

    // Invalidate programs that are associated with the guides
    const guideKeys = Array.from(guideIds).map((guideId) =>
        guidesQueryOptions.queryKey({
            guideIds: new Set([guideId]),
            circleId: circleId,
        })
    );
    const cachedGuides = guideKeys
        .map((key) => {
            const queryState =
                queryClient.getQueryState<MediaGuideWithSubtitles[]>(key);
            const cachedItem = queryState?.data;
            return cachedItem?.[0];
        })
        .filter((item) => typeof item !== 'undefined');
    const relatedProgramIds = cachedGuides.reduce<Set<string>>((acc, guide) => {
        const programId = guide?.programId;
        if (!programId) return acc;
        return acc.add(programId);
    }, new Set<string>());

    // A guide's own entry can be collected while the program that embeds it is still fresh.
    const embeddingPrograms = queryClient.getQueriesData<
        MediaProgramWithGuides[]
    >({
        queryKey: [queryKeys.programs, circleId],
        predicate: (query) => programsEmbedGuides(query.state.data, guideIds),
    });
    embeddingPrograms.forEach(([, programs]) =>
        programs?.forEach((program) => {
            if (guidesInProgram(program, guideIds)) {
                relatedProgramIds.add(program.programId);
            }
        })
    );
    await invalidatePrograms({ programIds: relatedProgramIds, circleId });

    if (!skipGuides) {
        // Invalidate guides by their IDs
        guideIds.forEach((guideId) => {
            void invalidateQueryKey(
                guidesQueryOptions.queryKey({
                    guideIds: new Set([guideId]),
                    circleId: circleId,
                })
            );
        });
    }

    // Invalidate packs that are associated with the guides
    const relatedPacks = queryClient.getQueriesData<MediaPackType[]>({
        queryKey: [queryKeys.packsByPackClass, circleId],
        predicate: (query) => {
            if (!query.queryKey) {
                return false;
            }

            // Ignore batch query keys -- we'll invalidate their pieces
            const packClassObject = query.queryKey[2] as Record<string, string>;
            const packClasses =
                packClassObject && typeof packClassObject === 'object'
                    ? Object.keys(packClassObject)
                    : [];
            if (packClasses.length !== 1) {
                return false;
            }

            // Ignore already-invalidated queries
            const queryState = query.state;
            const isValid =
                queryState &&
                !queryState.isInvalidated &&
                queryState.dataUpdatedAt &&
                queryState.dataUpdatedAt > Date.now() - MEDIA_STALE_TIME;
            if (!isValid) {
                return false;
            }

            // Find all packs that contain the guides
            const packs = queryState.data;
            const pack = packs && Array.isArray(packs) ? packs[0] : undefined;
            if (!pack) {
                return false;
            }
            const guideIsInPack = pack.items?.some((item) => {
                return item.type === 'guide' && guideIds.has(item.guideId);
            });
            return guideIsInPack;
        },
    });
    const relatedPackClasses = relatedPacks.reduce<string[]>(
        (acc, [, packs]) => {
            const packClasses = packs?.map((pack) => pack.packClass) ?? [];
            return acc.concat(packClasses);
        },
        []
    );
    invalidatePacks({ packClasses: relatedPackClasses, circleId });

    // Invalidate PopularWithListeners so the BFF-driven carousel reflects
    // the updated favorite state without waiting for stale-time expiry.
    void invalidateQueryKey([queryKeys.bffPopularWithListeners, circleId]);

    // Invalidate ForYou so the dynamic home "For You" carousel reflects
    // the updated favorite state without waiting for stale-time expiry.
    void invalidateQueryKey([queryKeys.forYouRecommendations, circleId]);
}

export function useGuides(_guideIds: string[], _circleId?: number) {
    const { activeCircleId } = useActiveCircleId();
    const circleId = _circleId || activeCircleId;
    const { getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } = guidesQueryOptions;
    const rHGApp = getRhgAppHeader();
    const guideIds = useMemo(() => new Set(_guideIds), [_guideIds]);

    const options = useMemo(
        () =>
            queryOptions({
                guideIds,
                circleId,
                circleAuthorizationToken,
            }),
        [circleAuthorizationToken, circleId, guideIds, queryOptions]
    );

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey({
            guideIds,
            circleId,
        }),
        queryFn: async () => {
            if (!guideIds.size) return [];
            const _guideIdsArray = Array.from(guideIds);

            const keyObjects = _guideIdsArray.map(
                (guideId): ItemToQueryMapping<string> => ({
                    item: guideId,
                    queryKey: queryKey({
                        guideIds: new Set([guideId]),
                        circleId,
                    }),
                })
            );

            const { cachedQueries, invalidQueries } = checkQueryCache<
                string,
                MediaGuideWithSubtitles[]
            >(keyObjects, options.staleTime);
            const cachedQueriesData = cachedQueries.reduce<
                MediaGuideWithSubtitles[]
            >((cachedData, item) => [...cachedData, ...item.data], []);

            // If all guides are already fetched and not stale, return them
            if (invalidQueries.length === 0) {
                return cachedQueriesData;
            }

            const guideIdsToFetch = invalidQueries.map(({ item }) => item);
            const response = await queryFn({
                getAccessTokenSilently,
                guideIds: new Set(guideIdsToFetch),
                circleId,
                circleAuthorizationToken,
                rHGApp,
            });

            // populate cache for program metadata
            const programs = response ?? [];
            cacheProgramMetadata(programs, circleId);
            // populate cache for each guide
            const guides = programs
                .flatMap((p) => p.guides)
                .filter((g) => typeof g !== 'undefined');
            cacheGuides(guides, circleId);

            // Combine newly fetched packs with existing packs
            return [...cachedQueriesData, ...(guides || [])];
        },
        ...options,
    });

    return {
        guides: data,
        isEnabled: options.enabled,
        ...otherProps,
    };
}
